/**
 * DocumentsPane — the "docs" perspective (§4.15).
 *
 * Hosts the CodeMirror 6 {@link MarkdownEditor} for the selected document — the
 * single way to view AND edit `.md` (Obsidian-style live preview) — flanked by
 * an outline rail (section jumps) and a "mentioned in" backlinks rail. The
 * document list lives in the unified workspace nav (Documents tab, by title), so
 * selection flows through the store like every other entity. Selection is
 * controlled so an `archlang://doc/<name>#<slug>` deep-link opens a document +
 * scrolls to a section; falls back to the first document when nothing is
 * selected. A centred empty state shows when the workspace declares no docs.
 */

import { useEffect, useMemo, useState } from "react";
import type {
  WireBacklink,
  WireMarkdownDocument,
} from "@archlang/viewer-core";
import { IconButton, useContextMenu, type ContextMenuItem } from "@archlang/ui";
import { useTranslation } from "../i18n";
import { MarkdownEditor } from "../workbench/primitives/md-editor/MarkdownEditor";
import { computeDocRename } from "../workbench/primitives/md-editor/renameDoc";
import { renameDocFile } from "./renameDocFile";
import { Backlinks } from "../components/Backlinks";
import {
  useOptionalWorkspace,
  type useWorkspace,
} from "../workbench/WorkspaceContext.js";
import { useWorkspaceFiles } from "../workbench/WorkspaceStore";
import {
  parseFrontmatter,
  serializeFrontmatter,
  setFrontmatterField,
  type FrontmatterField,
} from "./frontmatter";

type Store = ReturnType<typeof useWorkspace>;

/** Settings (gear) cog — the toggle for the properties panel. */
function GearIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-4 h-4"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

/** A list / table-of-contents glyph for the mobile "on this page" FAB. */
function ListIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-5 h-5"
      aria-hidden="true"
    >
      <line x1="8" y1="6" x2="20" y2="6" />
      <line x1="8" y1="12" x2="20" y2="12" />
      <line x1="8" y1="18" x2="20" y2="18" />
      <line x1="3.5" y1="6" x2="3.5" y2="6" />
      <line x1="3.5" y1="12" x2="3.5" y2="12" />
      <line x1="3.5" y1="18" x2="3.5" y2="18" />
    </svg>
  );
}

/**
 * Editable list of the document's frontmatter `key: value` pairs. Lives in the
 * header (the CM body no longer renders the `---`…`---` block). Each edit
 * rewrites the file's leading frontmatter block via the store; adding a property
 * to a doc with none creates the block.
 */
/** Turn a raw `key: value` text block back into ordered frontmatter fields. */
function fieldsFromText(text: string): FrontmatterField[] {
  const out: FrontmatterField[] = [];
  for (const line of text.split("\n")) {
    if (!line.trim()) continue;
    const i = line.indexOf(":");
    if (i === -1) out.push({ key: line.trim(), value: "" });
    else out.push({ key: line.slice(0, i).trim(), value: line.slice(i + 1).trim() });
  }
  return out;
}

function PropertiesPanel({ store, path }: { store: Store; path: string }) {
  const files = useWorkspaceFiles(store);
  const source = files.get(path) ?? "";
  // Show the frontmatter as a single editable `key: value` text block — easier
  // to read and reason about than a structured form. Commit on blur.
  const text = useMemo(
    () => parseFrontmatter(source).fields.map((f) => `${f.key}: ${f.value}`).join("\n"),
    [source],
  );
  const [draft, setDraft] = useState(text);
  useEffect(() => setDraft(text), [text]);

  const commit = (): void => {
    const next = serializeFrontmatter(source, fieldsFromText(draft));
    if (next === source) return;
    store.edit(path, next);
  };

  return (
    <div className="border-b border-slate-800/(--tint-strong) bg-slate-900/(--tint-dim) px-6 py-3 sm:px-8">
      <div className="mx-auto max-w-[56rem]">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">
          Properties
        </div>
        <textarea
          aria-label="Document properties"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          spellCheck={false}
          rows={Math.max(draft.split("\n").length, 1)}
          placeholder="key: value"
          className="w-full resize-y rounded border border-slate-700 bg-slate-800/(--tint-dim) px-2.5 py-1.5 font-mono text-xs leading-5 text-slate-200 placeholder:text-slate-600 focus:border-sky-500 focus:outline-none"
        />
      </div>
    </div>
  );
}

/**
 * One in-place editable frontmatter meta field (author / status / date) shown in
 * the titlebar. Reads its value live from the file's frontmatter and commits the
 * single key on blur (creating the block / key when absent, clearing on empty).
 */
function MetaField({
  store,
  path,
  fieldKey,
}: {
  store: Store;
  path: string;
  fieldKey: string;
}) {
  const files = useWorkspaceFiles(store);
  const value = useMemo(() => {
    const f = parseFrontmatter(files.get(path) ?? "").fields.find(
      (x) => x.key === fieldKey,
    );
    return f?.value ?? "";
  }, [files, path, fieldKey]);
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  const commit = (): void => {
    const src = store.getState().files.get(path) ?? "";
    const next = setFrontmatterField(src, fieldKey, draft.trim());
    if (next !== src) store.edit(path, next);
  };

  return (
    <input
      aria-label={`Document ${fieldKey}`}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.currentTarget.blur();
        } else if (e.key === "Escape") {
          e.preventDefault();
          setDraft(value);
          e.currentTarget.blur();
        }
      }}
      spellCheck={false}
      placeholder={fieldKey}
      size={Math.max(draft.length, fieldKey.length, 1)}
      className="bg-transparent text-right text-slate-400 outline-none focus:text-slate-200 placeholder:text-slate-700"
    />
  );
}

/** Characters that would break a file path or a `[[ref]]`/`#section` anchor. */
const INVALID_NAME = /[/\\[\]#]/;

/**
 * The editable filename, shown inline beside the settings gear. Renaming mirrors
 * the editor's F2 semantics: when the doc has NO title its ref name IS the stem,
 * so `computeDocRename` rewrites inbound refs AND yields the file rename; with a
 * title present the path doesn't affect the ref name, so we just `renameFile`.
 */
function DocFilename({ store, path }: { store: Store; path: string }) {
  const fileName = path.split("/").pop() ?? path;
  const dir = path.slice(0, path.length - fileName.length);
  const [draft, setDraft] = useState(fileName);
  useEffect(() => setDraft(fileName), [fileName]);

  const commit = (): void => {
    let next = draft.trim();
    if (!next || next === fileName || INVALID_NAME.test(next)) {
      setDraft(fileName);
      return;
    }
    if (!/\.(md|markdown)$/i.test(next)) next = `${next}.md`;
    const newPath = `${dir}${next}`;
    if (newPath === path) {
      setDraft(fileName);
      return;
    }
    // `renameDocFile` owns the whole law now — including "a titled doc is named
    // by its title, so a file rename touches neither the title nor any ref",
    // which used to be branched here and was therefore missing from the nav.
    const applied = renameDocFile(store, path, newPath);
    if (!applied) {
      // Refused (the name is taken, or is a directory) — put the box back to
      // the real name rather than leaving the user's text over an edit that
      // never happened.
      setDraft(fileName);
      return;
    }
    store.setActiveFile(newPath);
  };

  return (
    <input
      aria-label="Document filename"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.currentTarget.blur();
        } else if (e.key === "Escape") {
          e.preventDefault();
          setDraft(fileName);
          e.currentTarget.blur();
        }
      }}
      spellCheck={false}
      size={Math.max(draft.length, 1)}
      className="bg-transparent font-mono text-[11px] text-slate-500 outline-none focus:text-slate-300"
    />
  );
}

/**
 * The editable document heading: a small filename line above a large title.
 *
 * Both mirror the editor's F2 rename semantics (see MarkdownEditor `doRename`):
 *
 * - **Title** is the doc's display name = frontmatter `title:` (or the file stem
 *   when absent — core `parseMarkdownDocument`). Committing writes `title:` into
 *   the frontmatter and, because the doc's ref name now equals the new title,
 *   rewrites every inbound `[[oldName]]` across the workspace via
 *   `computeDocRename(files, oldName, newTitle, path)`. With a title present the
 *   plan never returns a file rename (name ≠ stem), so we apply only `edits`.
 *
 * - **Filename** renames the file. When the doc has NO title its ref name IS the
 *   stem, so `computeDocRename(files, oldStem, newStem, path)` both rewrites
 *   inbound refs AND yields the file rename — we apply both. When the doc HAS a
 *   title the ref name is unaffected by the path, so we just `renameFile`.
 *
 * Read-only (plain text) when there is no workspace `store`.
 */
function DocHeading({
  store,
  path,
  name,
}: {
  store: Store | null;
  path: string;
  name: string;
}) {
  // Local draft state, re-seeded whenever the doc's identity changes.
  const [titleDraft, setTitleDraft] = useState(name);
  useEffect(() => setTitleDraft(name), [name]);

  const readOnly = !store;

  const commitTitle = (): void => {
    const next = titleDraft.trim();
    if (!store || !next || next === name || INVALID_NAME.test(next)) {
      setTitleDraft(name);
      return;
    }
    // 1) Write the title into frontmatter so the doc's name becomes `next`.
    const src = store.getState().files.get(path) ?? "";
    store.edit(path, setFrontmatterField(src, "title", next));
    // 2) Rewrite every inbound `[[oldName]]` → `[[next]]`. The title already
    //    exists now, so the plan carries no file rename — apply edits only.
    const plan = computeDocRename(store.getState().files, name, next, path);
    for (const [p, c] of plan.edits) store.edit(p, c);
    // The tab is keyed by file path, so the title change needs no tab re-key.
  };

  if (readOnly) {
    return (
      <div className="mx-auto w-fit max-w-[56rem]">
        <h1 className="text-3xl font-semibold text-slate-100">{name}</h1>
      </div>
    );
  }

  return (
    // Full readable-column width, so meta can right-align to the CONTENT (not the
    // narrower centred title block). px-4 keeps the same min side gutter as the
    // prose lines when the pane is narrow.
    <div className="mx-auto w-full max-w-[56rem] px-4">
      {/* Centred title (wraps to multiple lines). */}
      <div className="w-full text-center">
        <textarea
          aria-label="Document title"
          value={titleDraft}
          rows={1}
          onChange={(e) => setTitleDraft(e.target.value)}
          onBlur={commitTitle}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault(); // a title is one logical line — Enter commits
              e.currentTarget.blur();
            } else if (e.key === "Escape") {
              e.preventDefault();
              setTitleDraft(name);
              e.currentTarget.blur();
            }
          }}
          spellCheck={false}
          // rows=1 keeps it visible everywhere; `field-sizing:content` auto-grows
          // it to wrap long titles (no JS height measuring → works even while the
          // header host is detached before CM adopts it).
          className="block w-full resize-none overflow-hidden bg-transparent text-center text-3xl font-semibold leading-tight text-slate-100 outline-none [field-sizing:content]"
        />
      </div>
      {/* Author on its own line, then status + date — right-aligned to the
          content column, in-place editable, values only. */}
      <div className="mt-2 text-right text-base text-slate-400 leading-relaxed">
        <div>
          <MetaField store={store!} path={path} fieldKey="author" />
        </div>
        <div className="flex justify-end items-baseline">
          <MetaField store={store!} path={path} fieldKey="status" />
          <span>,&nbsp;</span>
          <MetaField store={store!} path={path} fieldKey="date" />
        </div>
      </div>
    </div>
  );
}

export interface DocumentsPaneProps {
  readonly documents: readonly WireMarkdownDocument[];
  readonly backlinks?: Readonly<Record<string, readonly WireBacklink[]>>;
  /** Controlled selection — the document `name` currently open. */
  readonly selectedDocName: string | null;
  /** Section slug to scroll to (from an `archlang://doc/<name>#<slug>` link). */
  readonly scrollToSlug?: string;
  readonly onArchlangNav?: (href: string) => void;
}

export function DocumentsPane({
  documents,
  backlinks,
  selectedDocName,
  scrollToSlug,
  onArchlangNav,
}: DocumentsPaneProps) {
  const selected = useMemo(
    () =>
      documents.find((d) => d.name === selectedDocName) ?? documents[0] ?? null,
    [documents, selectedDocName],
  );

  // Scroll target = the latest of a deep-link anchor (prop) and an outline
  // click; the nonce makes repeat clicks re-fire even on the same section. It is
  // scoped to the open doc (`docKey`) so switching documents drops a pending
  // anchor from the previous doc instead of mis-scrolling the new one to it.
  const docKey = selected?.path ?? null;
  const [scroll, setScroll] = useState<{
    slug?: string;
    nonce: number;
    docKey: string | null;
  }>({ slug: scrollToSlug, nonce: 0, docKey });
  useEffect(() => {
    // On a doc switch, re-seed the target to this doc's deep-link slug (possibly
    // undefined → no scroll). A deep-link to the SAME doc still re-fires.
    setScroll((s) =>
      s.docKey === docKey
        ? scrollToSlug
          ? { slug: scrollToSlug, nonce: s.nonce + 1, docKey }
          : s
        : { slug: scrollToSlug, nonce: s.nonce + 1, docKey },
    );
  }, [scrollToSlug, docKey]);
  const jump = (slug: string) =>
    setScroll((s) => ({ slug, nonce: s.nonce + 1, docKey }));

  // Store access (null when DocumentsPane is rendered outside a workspace, e.g.
  // some unit tests) — gates the editable properties panel.
  const store = useOptionalWorkspace();
  const { t } = useTranslation("nav");
  // Right-click menu for the document header chrome (not the CM editor body,
  // which owns its own text-editing menu). Clipboard actions are always safe.
  const menu = useContextMenu();
  // Properties panel toggle, reset when switching documents.
  const [showProps, setShowProps] = useState(false);
  // Mobile "on this page" drawer (the desktop outline rail is always visible).
  const [showToc, setShowToc] = useState(false);
  useEffect(() => {
    setShowProps(false);
    setShowToc(false);
  }, [docKey]);

  if (documents.length === 0) {
    return (
      <main className="w-full h-full flex items-center justify-center bg-slate-950 text-slate-400">
        <div className="text-center max-w-md px-6">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">
            Documents
          </div>
          <h1 className="text-2xl font-semibold text-slate-100 mb-2">
            No documents declared
          </h1>
          <p className="text-sm leading-relaxed">
            Add a <code className="text-sky-300">.md</code> file to the workspace
            to write prose that cross-references your modules, interfaces, and
            processes — it will appear here.
          </p>
        </div>
      </main>
    );
  }

  const docBacklinks = selected ? backlinks?.[selected.name] ?? [] : [];

  // The document header (filename + title + gear/properties) rides INSIDE the
  // editor's scroller (via MarkdownEditor's `header` portal) so it scrolls with
  // the prose and has no separator from the body — a single continuous page.
  // Document menu — empty (no menu) when nothing is selected.
  const headerMenuItems: ContextMenuItem[] = selected
    ? [
        {
          label: t("menu.copyName"),
          onClick: () => void navigator.clipboard?.writeText(selected.name),
        },
        {
          label: t("menu.copyPath"),
          onClick: () => void navigator.clipboard?.writeText(selected.path),
        },
      ]
    : [];

  const docHeader = selected && (
    // The header widget spans the full scroller width; the gear + filename sit
    // inline at the top-left, the title centres over the readable column.
    <div>
      {/* Top-left: settings gear, then the filename inline right after it.
          A right-click on this header chrome opens the document menu (clipboard
          actions). The CM editor body below keeps its own text-editing menu. */}
      <div
        className="flex items-center gap-2 px-2 pt-2"
        onContextMenu={(e) => menu.open(e, headerMenuItems)}
      >
        <IconButton
          size="sm"
          aria-label="Toggle document properties"
          aria-pressed={showProps}
          onClick={() => setShowProps((v) => !v)}
          disabled={!store}
          className={`shrink-0 ${showProps ? "text-sky-400" : ""}`}
        >
          <GearIcon />
        </IconButton>
        {store && <DocFilename store={store} path={selected.path} />}
      </div>
      <div className="pt-4 pb-6">
        <DocHeading store={store} path={selected.path} name={selected.name} />
      </div>
      {showProps && store && <PropertiesPanel store={store} path={selected.path} />}
    </div>
  );

  return (
    <main className="min-h-0 flex-1 flex overflow-hidden bg-slate-950 text-slate-100">
      {selected && (
        <>
          {/* Full-width editor column: CM owns the scroll (page-wide scrollbar +
              viewport virtualization), the theme centres the text column (4xl) —
              the empty sides are the "side fields". The header is portaled into
              the scroller so it scrolls WITH the prose, no separator. */}
          <div className="min-w-0 flex-1 overflow-hidden">
            <MarkdownEditor
              key={selected.path}
              path={selected.path}
              onArchlangNav={onArchlangNav}
              scrollToSlug={scroll.slug}
              scrollNonce={scroll.nonce}
              header={docHeader}
              className="h-full"
            />
          </div>

          {/* Desktop outline rail — visible at md+ (the app's non-mobile width). */}
          {(selected.sections.length > 0 || docBacklinks.length > 0) && (
            <aside className="hidden md:block w-60 shrink-0 overflow-y-auto border-l border-slate-800/(--tint-strong) px-4 py-5">
              {tocContent(false)}
            </aside>
          )}

          {/* Mobile (<md): a top-right corner menu opens the "on this page"
              drawer. The responsive hide is on THIS wrapper (display:block→none
              at md) so it can't lose the cascade to the button's own `flex`
              (which would fight `md:hidden` on the same element). */}
          {(selected.sections.length > 0 || docBacklinks.length > 0) && (
            <div className="md:hidden">
              <IconButton
                size="md"
                aria-label="On this page"
                onClick={() => setShowToc(true)}
                className="fixed top-2.5 right-2.5 z-chrome border border-slate-700 bg-slate-900/(--tint-heavy) backdrop-blur"
              >
                <ListIcon />
              </IconButton>
              {showToc && (
                <div className="fixed inset-0 z-scrim" role="dialog" aria-modal="true">
                  <button
                    type="button"
                    aria-label="Close"
                    onClick={() => setShowToc(false)}
                    className="absolute inset-0 bg-black/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
                  />
                  <aside className="absolute inset-y-0 right-0 w-72 max-w-[80vw] overflow-y-auto border-l border-slate-800 bg-slate-950 px-4 py-5 shadow-2xl">
                    {tocContent(true)}
                  </aside>
                </div>
              )}
            </div>
          )}
        </>
      )}
      {menu.node}
    </main>
  );

  function tocContent(closeOnJump: boolean) {
    return (
      <>
        {selected!.sections.length > 0 && (
          <nav aria-label="Outline" className="mb-6">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">
              On this page
            </div>
            <ul className="space-y-0.5">
              {selected!.sections.map((s, i) => (
                <li key={`${s.slug}-${i}`}>
                  <button
                    type="button"
                    onClick={() => {
                      jump(s.slug);
                      if (closeOnJump) setShowToc(false);
                    }}
                    onContextMenu={(e) =>
                      menu.open(e, [
                        {
                          label: t("menu.goToSection"),
                          onClick: () => {
                            jump(s.slug);
                            if (closeOnJump) setShowToc(false);
                          },
                        },
                        {
                          label: t("menu.copyHeading"),
                          onClick: () =>
                            void navigator.clipboard?.writeText(s.text),
                        },
                      ])
                    }
                    className="text-left text-sm text-sky-300 hover:text-sky-200 hover:underline underline-offset-2 truncate w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
                    style={{ paddingLeft: Math.max(0, s.level - 1) * 12 }}
                  >
                    {s.text}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        )}
        <Backlinks backlinks={docBacklinks} onNavigate={onArchlangNav} />
      </>
    );
  }
}
